import { useEffect, useState } from "react";
import type { RefObject } from "react";

const TOP_RESET_THRESHOLD = 1;
const DIRECTION_THRESHOLD = 8;

export function useSmartStickyToolbar(toolbarRef: RefObject<HTMLElement | null>) {
  const [isSticky, setIsSticky] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    const toolbar = toolbarRef.current;
    const scrollContainer = toolbar?.closest(
      "[data-workspace-scroll-container]",
    );

    if (!toolbar || !(scrollContainer instanceof HTMLElement)) {
      return;
    }

    let previousScrollTop = Math.max(0, scrollContainer.scrollTop);
    const headerBottom =
      scrollContainer.parentElement
        ?.querySelector(':scope > [data-slot="workspace-header"]')
        ?.getBoundingClientRect().bottom ?? scrollContainer.getBoundingClientRect().top;
    const naturalExitScrollTop = Math.max(
      TOP_RESET_THRESHOLD,
      previousScrollTop
        + toolbar.getBoundingClientRect().bottom
        - headerBottom,
    );
    const updateContentClip = () => {
      const content = toolbar.firstElementChild;

      if (!(content instanceof HTMLElement)) {
        return;
      }

      const inset = Math.max(0, Math.ceil(headerBottom - content.getBoundingClientRect().top));

      if (inset > 0) {
        toolbar.style.setProperty("--toolbar-content-clip-top", `${inset}px`);
        toolbar.dataset.contentClipped = "true";
      } else {
        toolbar.style.removeProperty("--toolbar-content-clip-top");
        delete toolbar.dataset.contentClipped;
      }
    };
    let stickyActive = previousScrollTop >= naturalExitScrollTop;
    let direction = 0;
    let directionDistance = 0;
    let transitionFrame: number | null = null;

    const enableTransitionAfterPaint = () => {
      setShouldAnimate(false);

      if (transitionFrame !== null) {
        cancelAnimationFrame(transitionFrame);
      }

      transitionFrame = requestAnimationFrame(() => {
        transitionFrame = requestAnimationFrame(() => {
          setShouldAnimate(true);
          transitionFrame = null;
        });
      });
    };

    setIsSticky(stickyActive);
    setIsVisible(!stickyActive);
    updateContentClip();

    if (stickyActive) {
      enableTransitionAfterPaint();
    }

    const handleScroll = () => {
      const scrollTop = Math.max(0, scrollContainer.scrollTop);
      const delta = scrollTop - previousScrollTop;

      if (scrollTop <= TOP_RESET_THRESHOLD) {
        stickyActive = false;
        toolbar.style.removeProperty("--toolbar-content-clip-top");
        delete toolbar.dataset.contentClipped;
        setShouldAnimate(false);
        setIsSticky(false);
        setIsVisible(true);
        direction = 0;
        directionDistance = 0;
        previousScrollTop = scrollTop;
        return;
      }

      if (!stickyActive) {
        if (scrollTop >= naturalExitScrollTop) {
          stickyActive = true;
          enableTransitionAfterPaint();
          setIsSticky(true);
          setIsVisible(false);
          direction = 0;
          directionDistance = 0;
        } else {
          updateContentClip();
        }

        previousScrollTop = scrollTop;
        return;
      }

      const nextDirection = Math.sign(delta);

      if (nextDirection === 0) {
        return;
      }

      if (nextDirection !== direction) {
        direction = nextDirection;
        directionDistance = 0;
      }

      directionDistance += Math.abs(delta);

      if (directionDistance >= DIRECTION_THRESHOLD) {
        setIsVisible(direction < 0);
        directionDistance = 0;
      }

      previousScrollTop = scrollTop;
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);

      if (transitionFrame !== null) {
        cancelAnimationFrame(transitionFrame);
      }
    };
  }, [toolbarRef]);

  return { isSticky, isVisible, shouldAnimate };
}
